import type {
  DeferredPaymentReference,
  ElementsSubmitError,
  ElementsErrorCode,
  ElementsErrorEvent,
  PaypercutBillingDetails,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';
import { billingDetailsEqual } from './billing-details';
import { generateId } from '../utils/generate-id';
import { ElementsActionShell } from './action-shell';
import type {
  ElementsComponentEventPayload,
  ElementsSubmissionCancellationReason,
} from './controller-protocol';

const MILLISECONDS_PER_MINUTE = 60_000;
const SUBMIT_TIMEOUT_MINUTES = 10;
export const PAYMENT_METHOD_SUBMISSION_TIMEOUT_MS =
  SUBMIT_TIMEOUT_MINUTES * MILLISECONDS_PER_MINUTE;

/**
 * Private adapter implemented by any source that can produce a Paypercut
 * Payment Method. Keeping this contract source-neutral lets card collections
 * and future APM Elements join the same group-owned submission lifecycle.
 */
export interface PaymentMethodSubmissionSource {
  readonly submissionSourceId: string;
  validatePaymentMethod(): Promise<void>;
  assertCanSubmitPaymentMethod(): void;
  beginPaymentMethodSubmission(
    requestId: string,
    deadlineAt: number,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
    billingDetails?: PaypercutBillingDetails,
  ): void;
  cancelPaymentMethodSubmission(
    requestId: string,
    reason: ElementsSubmissionCancellationReason,
  ): void;
  attachPaymentMethodActionChannel(requestId: string, actionId: string, port: MessagePort): void;
  onPaymentMethodSubmissionStateChange(): void;
}

interface PendingSubmission {
  source: PaymentMethodSubmissionSource;
  requestId: string;
  futureUsage?: PaypercutPaymentMethodFutureUsage;
  billingDetails?: PaypercutBillingDetails;
  actionId: string | null;
  promise: Promise<DeferredPaymentReference>;
  resolve: (reference: DeferredPaymentReference) => void;
  reject: (error: ElementsSubmitError) => void;
  timeoutId: number;
}

type SubmissionEvent = Extract<
  ElementsComponentEventPayload,
  { event: 'action_required' | 'submit_result' }
>;

export class PaymentMethodSubmissionCoordinator {
  private pending: PendingSubmission | null = null;
  private indeterminateAttempt: { sourceId: string; requestId: string } | null = null;
  private completed: {
    sourceId: string;
    futureUsage?: PaypercutPaymentMethodFutureUsage;
    billingDetails?: PaypercutBillingDetails;
    reference: DeferredPaymentReference;
  } | null = null;
  private destroyed = false;
  private actionShell: ElementsActionShell | null = null;
  private futureUsageSelected = false;
  private selectedFutureUsage: PaypercutPaymentMethodFutureUsage | undefined;
  private billingDetailsSelected = false;
  private selectedBillingDetails: PaypercutBillingDetails | undefined;

  constructor(private readonly hostedOrigin: string) {}

  submit(
    source: PaymentMethodSubmissionSource,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
    billingDetails?: PaypercutBillingDetails,
  ): Promise<DeferredPaymentReference> {
    if (this.destroyed) {
      return Promise.reject(
        createSubmitError('elements_destroyed', 'The Elements group has been destroyed.', false),
      );
    }
    if (
      (this.futureUsageSelected && this.selectedFutureUsage !== futureUsage) ||
      (this.billingDetailsSelected &&
        !billingDetailsEqual(this.selectedBillingDetails, billingDetails))
    ) {
      return Promise.reject(submissionInputMismatchError());
    }
    // One Elements group owns one preparation result. Replacing or remounting
    // its visual source must never create a second Payment Method.
    if (this.completed) {
      if (
        this.completed.futureUsage !== futureUsage ||
        !billingDetailsEqual(this.completed.billingDetails, billingDetails)
      ) {
        return Promise.reject(submissionInputMismatchError());
      }
      return Promise.resolve({ ...this.completed.reference });
    }
    if (this.indeterminateAttempt) {
      return Promise.reject(
        createSubmitError(
          'elements_submit_indeterminate',
          'A previous submission has an unknown result. Create a new Elements group before making a deliberate new attempt.',
          false,
        ),
      );
    }
    if (this.pending) {
      if (this.pending.source.submissionSourceId === source.submissionSourceId) {
        if (
          this.pending.futureUsage !== futureUsage ||
          !billingDetailsEqual(this.pending.billingDetails, billingDetails)
        ) {
          return Promise.reject(submissionInputMismatchError());
        }
        return this.pending.promise;
      }
      return Promise.reject(
        createSubmitError(
          'elements_submit_in_progress',
          'Another payment method is already being prepared.',
          true,
        ),
      );
    }

    try {
      source.assertCanSubmitPaymentMethod();
    } catch (error) {
      return Promise.reject(normalizeSubmitError(error));
    }
    this.futureUsageSelected = true;
    this.selectedFutureUsage = futureUsage;
    this.billingDetailsSelected = true;
    this.selectedBillingDetails = billingDetails;

    const requestId = generateId();
    const deadlineAt = Date.now() + PAYMENT_METHOD_SUBMISSION_TIMEOUT_MS;
    let resolveSubmission!: (reference: DeferredPaymentReference) => void;
    let rejectSubmission!: (error: ElementsSubmitError) => void;
    const promise = new Promise<DeferredPaymentReference>((resolve, reject) => {
      resolveSubmission = resolve;
      rejectSubmission = reject;
    });
    const pending: PendingSubmission = {
      source,
      requestId,
      ...(futureUsage ? { futureUsage } : {}),
      ...(billingDetails ? { billingDetails } : {}),
      actionId: null,
      promise,
      resolve: resolveSubmission,
      reject: rejectSubmission,
      timeoutId: window.setTimeout(() => {
        if (this.pending !== pending) return;
        this.markIndeterminate(pending);
        this.cancelPendingSource(pending, 'timed_out');
        this.rejectPending(
          'elements_submit_timeout',
          'Preparing the payment details timed out and the result is unknown.',
          false,
        );
      }, PAYMENT_METHOD_SUBMISSION_TIMEOUT_MS),
    };
    this.pending = pending;
    source.onPaymentMethodSubmissionStateChange();

    try {
      if (billingDetails) {
        source.beginPaymentMethodSubmission(requestId, deadlineAt, futureUsage, billingDetails);
      } else if (futureUsage) {
        source.beginPaymentMethodSubmission(requestId, deadlineAt, futureUsage);
      } else {
        source.beginPaymentMethodSubmission(requestId, deadlineAt);
      }
    } catch {
      this.rejectPending('elements_submit_failed', 'Unable to submit the payment details.', true);
    }
    return promise;
  }

  handleSourceEvent(sourceId: string, payload: ElementsComponentEventPayload): void {
    if (payload.event === 'error') {
      this.handleSourceError(sourceId, payload);
      return;
    }
    if (payload.event !== 'action_required' && payload.event !== 'submit_result') return;
    this.handleSubmissionEvent(sourceId, payload);
  }

  handleSourceError(sourceId: string, error: ElementsErrorEvent): void {
    const pending = this.pending;
    if (pending?.source.submissionSourceId !== sourceId) return;

    // An out-of-band source error is not an authoritative submission result.
    // Once the request crossed the controller boundary, the SDK cannot prove
    // that the server mutation did not commit, so a retry in this group would
    // risk producing a second Payment Method.
    this.markIndeterminate(pending);
    this.cancelPendingSource(pending, 'failed');
    this.rejectPending(
      'elements_submit_indeterminate',
      error.code === 'elements_submit_indeterminate'
        ? error.message
        : 'The payment form became unavailable while its submission result was unknown.',
      false,
    );
  }

  hasActiveSubmission(sourceId?: string): boolean {
    if (sourceId === undefined) return this.pending !== null || this.indeterminateAttempt !== null;
    return (
      this.pending?.source.submissionSourceId === sourceId || this.indeterminateAttempt !== null
    );
  }

  getIndeterminateAttemptError(): ElementsSubmitError | null {
    if (!this.indeterminateAttempt) return null;
    return createSubmitError(
      'elements_submit_indeterminate',
      'A previous submission has an unknown result. Create a new Elements group before making a deliberate new attempt.',
      false,
    );
  }

  hasCompletedSubmission(_sourceId: string): boolean {
    return this.completed !== null;
  }

  ownsAttempt(sourceId: string, requestId: string): boolean {
    return (
      this.pending?.source.submissionSourceId === sourceId && this.pending.requestId === requestId
    );
  }

  releaseSource(sourceId: string): void {
    const pending = this.pending;
    if (pending?.source.submissionSourceId === sourceId) {
      this.markIndeterminate(pending);
      try {
        pending.source.cancelPaymentMethodSubmission(pending.requestId, 'destroyed');
      } catch {
        // Local teardown remains authoritative if the controller is already unavailable.
      }
      this.rejectPending(
        'elements_submit_indeterminate',
        'The payment form was removed while its submission result was unknown.',
        false,
      );
    }
  }

  reinitializeSource(sourceId: string): void {
    const pending = this.pending;
    if (!pending || pending.source.submissionSourceId !== sourceId) return;

    this.markIndeterminate(pending);
    try {
      pending.source.cancelPaymentMethodSubmission(pending.requestId, 'destroyed');
    } catch {
      // The replacement frame can still attach when the previous port is gone.
    }
    this.rejectPending(
      'elements_submit_indeterminate',
      'The secure payment form reloaded while its submission result was unknown.',
      false,
    );
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.pending) {
      this.markIndeterminate(this.pending);
      this.cancelPendingSource(this.pending, 'destroyed');
      this.rejectPending(
        'elements_submit_indeterminate',
        'The Elements group was destroyed while its submission result was unknown.',
        false,
      );
    }
    this.actionShell?.destroy();
    this.actionShell = null;
  }

  private handleSubmissionEvent(sourceId: string, payload: SubmissionEvent): void {
    const pending = this.pending;
    if (
      !pending ||
      pending.source.submissionSourceId !== sourceId ||
      payload.requestId !== pending.requestId
    ) {
      return;
    }

    if (payload.event === 'action_required') {
      pending.actionId = payload.action.id;
      const actionChannel = new MessageChannel();
      try {
        pending.source.attachPaymentMethodActionChannel(
          payload.requestId,
          payload.action.id,
          actionChannel.port1,
        );
        this.getActionShell().show(payload.action, actionChannel.port2);
        pending.source.onPaymentMethodSubmissionStateChange();
      } catch {
        actionChannel.port1.close();
        actionChannel.port2.close();
        this.cancelPendingSource(pending, 'failed');
        this.rejectPending(
          'elements_action_channel_unavailable',
          'The required payment authentication could not be opened.',
          true,
        );
      }
      return;
    }

    this.pending = null;
    window.clearTimeout(pending.timeoutId);
    this.actionShell?.close();
    if (payload.result.status === 'succeeded') {
      const reference: DeferredPaymentReference = {
        type: 'payment_method',
        id: payload.result.paymentMethod.id,
      };
      this.completed = {
        sourceId,
        ...(pending.futureUsage ? { futureUsage: pending.futureUsage } : {}),
        ...(pending.billingDetails ? { billingDetails: pending.billingDetails } : {}),
        reference,
      };
      pending.resolve({ ...reference });
    } else {
      if (payload.result.code === 'elements_submit_indeterminate') {
        this.indeterminateAttempt = { sourceId, requestId: pending.requestId };
      }
      pending.reject(
        createSubmitError(payload.result.code, payload.result.message, payload.result.recoverable),
      );
    }
    pending.source.onPaymentMethodSubmissionStateChange();
  }

  private cancelAction(actionId: string): void {
    const pending = this.pending;
    if (!pending || pending.actionId !== actionId) return;
    try {
      pending.source.cancelPaymentMethodSubmission(pending.requestId, 'canceled');
    } catch {
      // The local promise still settles if the controller has already gone away.
    }
    this.rejectPending('elements_action_canceled', 'Payment authentication was canceled.', true);
  }

  private failAction(actionId: string, code: string, message: string, recoverable: boolean): void {
    const pending = this.pending;
    if (!pending || pending.actionId !== actionId) return;
    if (code === 'elements_submit_indeterminate') this.markIndeterminate(pending);
    try {
      pending.source.cancelPaymentMethodSubmission(pending.requestId, 'failed');
    } catch {
      // The local submission still fails if the controller has already gone away.
    }
    this.rejectPending(code, message, recoverable);
  }

  private rejectPending(code: string, message: string, recoverable: boolean): void {
    const pending = this.pending;
    if (!pending) return;
    this.pending = null;
    window.clearTimeout(pending.timeoutId);
    this.actionShell?.close();
    pending.reject(createSubmitError(code, message, recoverable));
    pending.source.onPaymentMethodSubmissionStateChange();
  }

  private cancelPendingSource(
    pending: PendingSubmission,
    reason: ElementsSubmissionCancellationReason,
  ): void {
    try {
      pending.source.cancelPaymentMethodSubmission(pending.requestId, reason);
    } catch {
      // Local settlement remains authoritative if the hosted source is unavailable.
    }
  }

  private markIndeterminate(pending: PendingSubmission): void {
    this.indeterminateAttempt = {
      sourceId: pending.source.submissionSourceId,
      requestId: pending.requestId,
    };
  }

  private getActionShell(): ElementsActionShell {
    this.actionShell ??= new ElementsActionShell(
      this.hostedOrigin,
      (actionId) => this.cancelAction(actionId),
      (actionId, code, message, recoverable) =>
        this.failAction(actionId, code, message, recoverable),
    );
    return this.actionShell;
  }
}

const submissionInputMismatchError = (): ElementsSubmitError =>
  createSubmitError(
    'elements_component_configuration_invalid',
    'This Elements group has already started Payment Method creation with different futureUsage or billing details. Create a new Elements group to change either value.',
    false,
  );

export function createSubmitError(
  code: ElementsErrorCode,
  message: string,
  recoverable: boolean,
): ElementsSubmitError {
  const error = new Error(message) as ElementsSubmitError;
  error.name = 'ElementsSubmitError';
  error.code = code;
  error.recoverable = recoverable;
  return error;
}

const normalizeSubmitError = (error: unknown): ElementsSubmitError => {
  if (
    error instanceof Error &&
    'code' in error &&
    typeof error.code === 'string' &&
    'recoverable' in error &&
    typeof error.recoverable === 'boolean'
  ) {
    return error as ElementsSubmitError;
  }
  return createSubmitError(
    'elements_submit_failed',
    error instanceof Error ? error.message : 'Unable to submit the payment details.',
    true,
  );
};
